import { AppChannel } from "@/config/channel"
import useGlobalParamsStore from "@/store/global-params-store"
import { Err, isOk, Ok, Result } from "@pacote/result"
import useSWRMutation from "swr/mutation"
import { v4 as uuidV4 } from "uuid"
import { z } from "zod"
import { ApiFailure, UnhandledApiError } from "./api-config"
import { baseClientFetcher } from "./base-fetchers"
import { InfoMessageList, messagesFetcher } from "./get-messages"
import {
  BaseLoginPayload,
  ComputedLoginFields,
  computeErrorType,
  computeHasOther,
  computePostLoginAction,
  LoginErrorResponse,
  loginResponseSchema,
  TloginErrorResponse,
} from "./post-login"

type OAuthLoginPayload = BaseLoginPayload & {
  pvd?: string
}

const oAuthLoginResponseSchema = loginResponseSchema
  .omit({ token: true })
  .extend({
    jwt: z.string().optional(),
    expire: z.string().optional(),
    redirect: z.string().optional(),
  })

type OAuthLoginResponse = z.infer<typeof oAuthLoginResponseSchema>

type TOAuthLoginResponse = OAuthLoginResponse & ComputedLoginFields

type OAuthLoginResult = Result<TOAuthLoginResponse, TloginErrorResponse>

const oAuthLoginServiceEndpoint = "/authentication-ms/v1/oauth/login"

const oAuthLoginFetcher = async (
  key: string,
  { arg }: { arg: OAuthLoginPayload },
): Promise<OAuthLoginResult> => {
  const sessionId = uuidV4()

  const loginServiceResult = await baseClientFetcher<
    OAuthLoginResponse,
    LoginErrorResponse
  >(
    {
      url: key,
      method: "POST",
      headers: {
        "X-Session-Id": sessionId,
      },
      body: {
        username: arg.username,
        password: arg.password,
        pvd: arg.pvd ?? "BO9999",
        recaptchaToken: arg.recaptchaToken,
      },
      successSchemaValidator: oAuthLoginResponseSchema,
      throwOnValidationError: true,
    },
    { enrichRequestData: false },
  )

  if (isOk(loginServiceResult)) {
    sessionStorage.setItem("session-id", sessionId)

    const loginResponse = loginServiceResult.value

    const { jwt } = loginResponse

    let messages: InfoMessageList = []

    if (jwt) {
      useGlobalParamsStore.getState().setToken(jwt)

      if (arg.channel !== AppChannel.ClientDesktopSisalPoker) {
        const messagesServiceResult = await messagesFetcher()

        if (isOk(messagesServiceResult)) {
          messages = messagesServiceResult.value.infoMessageList
        }
      }
    }

    const postLoginAction = computePostLoginAction<OAuthLoginResponse>(
      loginResponse,
      arg.channel,
      messages,
    )

    const hasOther = computeHasOther<OAuthLoginResponse>(
      loginResponse,
      messages,
    )

    return Ok({
      ...loginResponse,
      postLoginAction,
      hasOther,
      messages,
    })
  }

  if (loginServiceResult.value.status !== 422) {
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `A non 422 httpStatus was returned on request: ${oAuthLoginServiceEndpoint}`,
    })
  }

  const errorResponse = loginServiceResult.value.data

  const errorType = computeErrorType(loginServiceResult.value)

  const counter = errorResponse.error?.errorInfos?.counter

  return Err({
    type: errorType,
    counter: 4,
    errorResponse,
    ...(Array.isArray(counter) && { counter: Number(counter[0]) + 1 }),
  })
}

const useOAuthLoginMutation = () =>
  useSWRMutation(oAuthLoginServiceEndpoint, oAuthLoginFetcher, {
    populateCache: true,
  })

export { oAuthLoginServiceEndpoint, useOAuthLoginMutation }
export type { TOAuthLoginResponse }
